/**
 * Credit Balance service — non-CRUD endpoints that bypass the
 * dataProvider. The list (`getList`) goes through dataProvider via the
 * resource mapping; everything here is for the modal interactions
 * (top-up, block, unblock) and dropdown loaders.
 *
 * Mirrors Angular's `CreditBalanceService` + the relevant
 * `CostCenterService` methods used by `credit-balance.component.ts`.
 */

import { httpClient } from '@/utils/httpClient';
import type { NotifyEnvelope } from '@/types/api';
import type { CostCenterOption } from '../types/creditBalance';

export const getAllCostCenters = async (): Promise<CostCenterOption[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<CostCenterOption[]>>(
    '/costcenter'
  );
  return data.Response ?? [];
};

export const getCostCentersWithoutBalance = async (): Promise<
  CostCenterOption[]
> => {
  const { data } = await httpClient.get<NotifyEnvelope<CostCenterOption[]>>(
    '/costcenter/without-balance'
  );
  return data.Response ?? [];
};

export const purchaseCredit = async (
  costCenterId: string,
  amount: number
): Promise<void> => {
  const qs = new URLSearchParams({
    costCenterId,
    amount: String(amount),
  }).toString();
  await httpClient.post(`/credit-transactions/topup?${qs}`, {});
};

export const blockBalance = async (costCenterId: string): Promise<void> => {
  await httpClient.post(
    `/credit-balances/block/${encodeURIComponent(costCenterId)}`,
    {}
  );
};

export const unblockBalance = async (costCenterId: string): Promise<void> => {
  await httpClient.post(
    `/credit-balances/unblock/${encodeURIComponent(costCenterId)}`,
    {}
  );
};

export const adjustBalance = async (
  costCenterId: string,
  newCredits: number
): Promise<void> => {
  const qs = new URLSearchParams({
    costCenterId,
    newCredits: String(newCredits),
  }).toString();
  await httpClient.post(`/credit-balances/adjust?${qs}`, {});
};
